import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AuthService } from 'src/app/services/auth/auth.service';

export type HistorySection = 'detalles' | 'ubicaciones' | 'materiales' | 'desechables' | 'modificadores'
    | 'salidas' | 'presentaciones' | 'opciones';
export type HistoryAction = 'create' | 'update' | 'delete';
/** Que le paso a un elemento de un arreglo. Ausente en campos escalares y en registros viejos. */
export type HistoryChangeType = 'added' | 'removed' | 'updated';

export interface HistoryChange {
    /** Texto completo (compatibilidad con registros viejos y busqueda). */
    campo: string;
    valorAnterior: string;
    valorNuevo: string;
    tipo?: HistoryChangeType;
    /** Estructura para agrupar en pantalla: "Materiales" › "Fresas" › "Cantidad". */
    grupo?: string | null;
    elemento?: string | null;
    atributo?: string | null;
}

export interface HistoryRecordOptions {
    /** Motivo visible en el historial (ej. "Costos de referencia faltantes"). */
    reason?: string | null;
}

interface ItemDescriber {
    id: (item: any) => string;
    name: (item: any) => string;
    details?: (item: any) => { label: string; value: string }[];
}

type Names = Record<string, string>;

interface EntityConfig {
    fields: Record<string, { label: string; format?: (value: any, names: Names) => string; section?: HistorySection }>;
    arrays: Record<string, { label: string; section: HistorySection; item: (names: Names) => ItemDescriber }>;
    /** Derivados, metadatos o candados: no son cambios que haga el usuario. */
    ignore: string[];
    /** Campos ignorados solo en ciertos documentos (copias de un campo que ya se registra aparte). */
    ignoreWhen?: (data: any) => string[];
    /** Coleccion de categorias de la entidad (para mostrar `category_key` por nombre). */
    categories?: string;
    label: (data: any) => string;
}

/**
 * Historial de cambios de materiales, recetas, recetas de muestra, modificadores y presentaciones
 * (fix/stroy). Se escribe desde la app: `ApiService` envuelve las escrituras de esas colecciones
 * (`track` / `recordCreate`) y las escrituras en batch que las tocan llaman a `record` directo.
 *
 * Compara el documento COMPLETO antes y despues (no una lista de campos): cualquier campo que
 * cambie queda registrado. Los campos conocidos se muestran con etiqueta y formato legible; los
 * arreglos (materiales, desechables, areas, ubicaciones, salidas, opciones...) elemento por
 * elemento como agregado / eliminado / modificado; un campo nuevo sin configuracion usa un
 * formato generico con su nombre.
 *
 * Forma del registro (la misma que ya leen las pantallas):
 *   `{doc}/history`: { usuario, accion, seccion, cambios: [{ campo, valorAnterior, valorNuevo, tipo? }],
 *                      motivo, created_at }
 *
 * Nunca rompe la escritura original: un error del historial solo se registra en consola.
 */
@Injectable({ providedIn: 'root' })
export class EntityHistoryService {

    constructor(private afs: AngularFirestore, private auth: AuthService) { }

    /** true si la coleccion lleva historial. */
    isTracked(collectionPath: string): boolean {
        return !!this.resolve(collectionPath, 'x');
    }

    /**
     * Ejecuta `write` registrando el cambio: lee el documento antes y despues (2 lecturas) para
     * comparar lo que realmente quedo guardado (incluye `FieldValue`, merges y borrados).
     */
    async track<T>(collectionPath: string, docId: string, write: () => Promise<T>, options: HistoryRecordOptions = {}): Promise<T> {
        if (!this.isTracked(collectionPath) || !docId) return write();
        // Si no se puede leer el "antes", se guarda igual y sin historial: nunca bloquea al usuario.
        let before: any;
        try {
            before = await this.readDoc(`${collectionPath}/${docId}`);
        } catch (error) {
            console.error('[historial] No se pudo leer el documento antes de guardar', error);
            return write();
        }
        const result = await write();
        try {
            const after = await this.readDoc(`${collectionPath}/${docId}`);
            await this.record(collectionPath, docId, before, after, options);
        } catch (error) {
            console.error('[historial] No se pudo registrar el cambio', error);
        }
        return result;
    }

    /** Alta ya escrita (el documento nuevo es exactamente `data`). */
    async recordCreate(collectionPath: string, docId: string, data: any, options: HistoryRecordOptions = {}): Promise<void> {
        try {
            await this.record(collectionPath, docId, null, data, options);
        } catch (error) {
            console.error('[historial] No se pudo registrar el alta', error);
        }
    }

    /** Compara `before` / `after` (null = no existia / fue eliminado) y escribe el historial. */
    async record(collectionPath: string, docId: string, before: any, after: any, options: HistoryRecordOptions = {}): Promise<void> {
        const target = this.resolve(collectionPath, docId);
        if (!target || (!before && !after)) return;
        const { config, historyDoc, accountKey } = target;
        const data = after ?? before;
        const prefix = target.prefix ? target.prefix(data) : '';
        const prefixed = (campo: string) => (prefix ? `${prefix} · ${campo}` : campo);
        const createSection = target.section ?? 'detalles';

        let accion: HistoryAction;
        let bySection = new Map<HistorySection, HistoryChange[]>();

        if (!before) {
            accion = 'create';
            bySection.set(createSection, [{
                campo: prefixed('Elemento creado'), valorAnterior: '', valorNuevo: config.label(after), tipo: 'added',
                grupo: target.group ?? null, elemento: config.label(after), atributo: null,
            }]);
        } else if (!after) {
            accion = 'delete';
            bySection.set(createSection, [{
                campo: prefixed('Elemento eliminado'), valorAnterior: config.label(before), valorNuevo: '', tipo: 'removed',
                grupo: target.group ?? null, elemento: config.label(before), atributo: null,
            }]);
        } else {
            accion = 'update';
            const keys = changedKeys(before, after, config);
            if (!keys.size) return;
            const names = await this.resolveNames(accountKey, config, before, after, keys);
            bySection = diffDocuments(before, after, config, names, keys, prefix,
                target.group ? { grupo: target.group, elemento: config.label(after) } : null);
        }

        await this.write(historyDoc, accion, bySection, options.reason ?? null);
    }

    /**
     * Tags de un material por area (viven en shards de `area_tags`, fuera del documento del
     * material). `changes` = materiales cuyo arreglo cambio.
     */
    async recordMaterialTags(areaLabel: string, changes: { materialKey: string; before: string[]; after: string[] }[]): Promise<void> {
        try {
            const accountKey = this.auth.account_key;
            const format = (tags: string[]) => ((tags || []).length ? [...tags].sort().join(', ') : 'Ninguna');
            for (const change of changes) {
                if (format(change.before) === format(change.after)) continue;
                await this.write(`accounts_abc/${accountKey}/materials/${change.materialKey}`, 'update', new Map([[
                    'ubicaciones' as HistorySection,
                    [{
                        campo: `Etiquetas · ${areaLabel}`, valorAnterior: format(change.before), valorNuevo: format(change.after),
                        tipo: 'updated' as HistoryChangeType, grupo: 'Etiquetas', elemento: areaLabel, atributo: 'Etiquetas',
                    }],
                ]]), null);
            }
        } catch (error) {
            console.error('[historial] No se pudieron registrar las etiquetas', error);
        }
    }

    async readDoc(path: string): Promise<any | null> {
        const snap = await this.afs.firestore.doc(path).get();
        return snap.exists ? snap.data() : null;
    }

    private async write(historyDoc: string, accion: HistoryAction, bySection: Map<HistorySection, HistoryChange[]>, motivo: string | null): Promise<void> {
        const batch = this.afs.firestore.batch();
        let count = 0;
        for (const [seccion, cambios] of bySection) {
            if (!cambios.length) continue;
            batch.set(this.afs.firestore.collection(`${historyDoc}/history`).doc(), {
                usuario: this.auth.user_resume,
                accion,
                seccion,
                cambios,
                motivo,
                created_at: new Date(),
            });
            count++;
        }
        if (count) await batch.commit();
    }

    /** Coleccion -> configuracion y documento que recibe el historial. */
    private resolve(collectionPath: string, docId: string): {
        config: EntityConfig; historyDoc: string; accountKey: string;
        prefix?: (data: any) => string; section?: HistorySection; group?: string;
    } | null {
        const path = (collectionPath || '').replace(/\/+$/, '');
        let m = path.match(/^accounts_abc\/([^/]+)\/(materials|sample_recipes|recipes|modifiers)$/);
        if (m) {
            const config = m[2] === 'recipes' ? RECIPE_CONFIG : m[2] === 'modifiers' ? MODIFIER_CONFIG : MATERIAL_CONFIG;
            return { config, historyDoc: `${path}/${docId}`, accountKey: m[1] };
        }
        m = path.match(/^accounts_abc\/([^/]+)\/materials\/([^/]+)\/presentations$/);
        if (m) {
            return {
                config: PRESENTATION_CONFIG,
                historyDoc: `accounts_abc/${m[1]}/materials/${m[2]}`,
                accountKey: m[1],
                prefix: (data) => `Presentacion ${data?.name ?? ''}`.trim(),
                section: 'presentaciones',
                group: 'Presentaciones',
            };
        }
        return null;
    }

    /** Lee solo los nombres de ids presentes en campos que cambiaron. */
    private async resolveNames(accountKey: string, config: EntityConfig, before: any, after: any, keys: Set<string>): Promise<Names> {
        const base = `accounts_abc/${accountKey}`;
        const names: Names = {};
        for (const doc of [before, after]) {
            if (doc?.category_key && doc?.category?.name) names[doc.category_key] = doc.category.name;
        }
        const paths = new Map<string, string>();
        const want = (id: any, path: string) => { if (typeof id === 'string' && id && !names[id]) paths.set(id, path); };

        if (keys.has('category_key') && config.categories) {
            [before?.category_key, after?.category_key].forEach((id) => want(id, `${base}/${config.categories}/${id}`));
        }
        if (keys.has('areas')) {
            [...idsOf(before?.areas), ...idsOf(after?.areas)].forEach((id) => want(id, `${base}/areas/${id}`));
        }
        if (keys.has('modifiers')) {
            [...idsOf(before?.modifiers), ...idsOf(after?.modifiers)].forEach((id) => want(id, `${base}/modifiers/${id}`));
        }
        if (keys.has('stock_config')) {
            for (const entry of [...(before?.stock_config || []), ...(after?.stock_config || [])]) {
                want(entry?.area_key, `${base}/areas/${entry?.area_key}`);
                want(entry?.location_key, `${base}/locations/${entry?.location_key}`);
            }
        }

        const entries = [...paths.entries()];
        const snaps = await Promise.all(entries.map(([, p]) => this.afs.firestore.doc(p).get().catch(() => null)));
        snaps.forEach((snap: any, i) => {
            const name = snap?.exists ? snap.data()?.name : null;
            if (name) names[entries[i][0]] = name;
        });
        return names;
    }
}

// ─── Formato ────────────────────────────────────────────────────────────────────────────────

const GLOBAL_IGNORE = [
    'last_seen_at', 'last_seen_by', 'updated_date', 'updated_at', 'creation_date', 'created_at',
    'created_date', '$key', 'account', 'account_key',
];

const ORDER_TYPES: Record<string, string> = { tables: 'Mesa', takeout: 'Llevar', delivery: 'Domicilio' };
const MEASURES: Record<string, string> = { mass: 'Masa', volume: 'Volumen', length: 'Longitud', pzs: 'Unidades' };
const EXPIRATION_TYPES: Record<string, string> = {
    by_provider: 'Fecha del proveedor', days_variation: 'Variacion de dias', no_apply: 'No aplica',
};
const MOVEMENT_TYPES: Record<string, string> = {
    inventory_usage: 'Con manejo de inventario', non_inventory_usage: 'Sin manejo de inventario',
};
const MATERIAL_TYPES: Record<string, string> = {
    commodity: 'Materia prima', 'semi-finished': 'Semielaborado', disposable: 'Desechable', cleaning: 'Limpieza',
};

const MAX_VALUE_LENGTH = 300;
const truncate = (text: string) => (text.length > MAX_VALUE_LENGTH ? `${text.slice(0, MAX_VALUE_LENGTH)}…` : text);

const num = (value: any): string => {
    if (value === null || value === undefined || value === '') return '';
    const n = Number(value);
    return Number.isFinite(n) ? String(Number(n.toFixed(6))) : String(value);
};
const money = (value: any): string => (value === null || value === undefined || value === '' ? '' : `$${Number(value).toFixed(2)}`);
const yesNo = (value: any): string => (value ? 'Si' : 'No');
const status = (value: any): string => (value === false ? 'Inactivo' : 'Activo');
const fromMap = (map: Record<string, string>) => (value: any) => (value ? map[value] ?? String(value) : '');

const isTimestamp = (value: any): boolean => !!value && typeof value === 'object' && typeof value.toDate === 'function';

function stableStringify(value: any): string {
    if (isTimestamp(value)) return JSON.stringify(value.toDate().toISOString());
    if (value instanceof Date) return JSON.stringify(value.toISOString());
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
    if (value && typeof value === 'object') {
        return `{${Object.keys(value).sort().filter((k) => value[k] !== undefined)
            .map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`;
    }
    return JSON.stringify(value ?? null);
}

/** Formato para campos sin configuracion: escalares, objetos con nombre y listas. */
function formatGeneric(value: any): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return yesNo(value);
    if (typeof value === 'number') return num(value);
    if (typeof value === 'string') return truncate(value);
    if (isTimestamp(value) || value instanceof Date) {
        const d = isTimestamp(value) ? value.toDate() : value;
        return d.toLocaleString('es-SV');
    }
    if (Array.isArray(value)) {
        if (!value.length) return 'Ninguno';
        return truncate(value.map((v) => (v && typeof v === 'object' ? formatGeneric(v) : String(v))).join(', '));
    }
    if (typeof value === 'object') {
        if (value.name && value.symbol) return `${value.name} (${value.symbol})`;
        if (value.name) return String(value.name);
        return truncate(stableStringify(value));
    }
    return String(value);
}

/** `expiry_time` -> "Expiry time" */
function humanize(key: string): string {
    const text = key.replace(/[_-]+/g, ' ').trim();
    return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Cantidad como la ve el usuario: con presentacion, el numero de presentaciones; sin ella,
 * `input_quantity` (formato actual) o `quantity` (legado, ya en la unidad de `metric`). Asi migrar
 * una linea legado al guardar no aparece como cambio.
 */
function formatLineQuantity(line: any): string {
    if (!line) return '';
    if (line.presentation) {
        const count = line.display_quantity ?? line.input_quantity
            ?? (line.presentation.quantity ? line.quantity / line.presentation.quantity : line.quantity);
        return `${num(count)} x ${line.presentation.name ?? 'presentacion'}`;
    }
    const qty = line.input_quantity ?? line.quantity;
    return `${num(qty)} ${line.metric?.symbol ?? line.material?.metric?.symbol ?? ''}`.trim();
}

function formatOrderTypes(types: any): string {
    if (!Array.isArray(types) || !types.length) return 'Ninguno';
    return Object.keys(ORDER_TYPES).filter((t) => types.includes(t)).map((t) => ORDER_TYPES[t]).join(', ');
}

const idsOf = (value: any): string[] => (Array.isArray(value)
    ? value.map((v) => (typeof v === 'string' ? v : v?.$key)).filter(Boolean)
    : []);

// ─── Elementos de arreglos ──────────────────────────────────────────────────────────────────

const materialId = (line: any): string => line?.material?.$key ?? line?.material_key ?? line?.$key ?? '';
const materialName = (line: any): string => line?.material?.name ?? line?.name ?? 'Material';

const Items = {
    recipeLine: (): ItemDescriber => ({
        id: materialId,
        name: materialName,
        details: (line) => [
            { label: 'Cantidad', value: formatLineQuantity(line) },
            { label: 'Tipos de orden', value: formatOrderTypes(line?.order_type) },
        ],
    }),
    semiFinishedLine: (): ItemDescriber => ({
        id: materialId,
        name: materialName,
        details: (line) => [
            { label: 'Cantidad', value: formatLineQuantity(line) },
            { label: 'Variable', value: yesNo(line?.variable) },
            { label: 'Sustituciones', value: (line?.substitutions || []).map((s: any) => s?.name).filter(Boolean).join(', ') || 'Ninguna' },
        ],
    }),
    disposableLine: (withOrderTypes: boolean) => (): ItemDescriber => ({
        id: materialId,
        name: materialName,
        details: (line) => [
            { label: 'Cantidad', value: `${num(line?.quantity)} ${line?.metric?.symbol ?? line?.material?.metric?.symbol ?? 'pzs'}`.trim() },
            ...(withOrderTypes ? [{ label: 'Tipos de orden', value: formatOrderTypes(line?.order_type) }] : []),
        ],
    }),
    output: (): ItemDescriber => ({
        id: (o) => (o?.master ? '__master__' : o?.material?.$key ?? ''),
        name: (o) => (o?.master ? 'Salida principal' : o?.material?.name ?? 'Salida'),
        details: (o) => [
            { label: 'Rendimiento', value: `${num(o?.performance)} ${o?.metric?.symbol ?? ''}`.trim() },
            ...(o?.master ? [] : [{ label: 'Incluir en costo', value: yesNo(o?.include_in_cost) }]),
        ],
    }),
    /** Arreglo de ids (o de objetos con `$key`) mostrados por nombre. */
    reference: (fallback: string) => (names: Names): ItemDescriber => ({
        id: (item) => (typeof item === 'string' ? item : item?.$key ?? ''),
        name: (item) => {
            const id = typeof item === 'string' ? item : item?.$key;
            return names[id] || item?.name || fallback;
        },
    }),
    stockArea: (names: Names): ItemDescriber => ({
        id: (a) => `${a?.location_key}_${a?.area_key}`,
        name: (a) => {
            const area = names[a?.area_key] ?? 'Area eliminada';
            const location = names[a?.location_key];
            return location ? `${area} (${location})` : area;
        },
        details: (a) => [
            { label: 'Minimos y maximos', value: yesNo(a?.min_max_enabled) },
            { label: 'Minimo', value: a?.min_max_enabled ? num(a?.min_inv) : '—' },
            { label: 'Alerta', value: a?.min_max_enabled ? num(a?.alert_min_inv) : '—' },
        ],
    }),
    modifierOption: (): ItemDescriber => ({
        id: (o) => o?.$key ?? o?.id ?? o?.name ?? '',
        name: (o) => o?.name ?? 'Opcion',
        details: (o) => [
            { label: 'Precio extra', value: money(o?.extra ?? o?.price ?? 0) },
            {
                label: 'Ingredientes',
                value: (o?.ingredients || [])
                    .map((i: any) => `${i?.material?.name ?? 'Material'} ${formatLineQuantity(i)}`.trim())
                    .join(', ') || 'Ninguno',
            },
        ],
    }),
};

// ─── Configuracion por entidad ──────────────────────────────────────────────────────────────

const categoryField = { label: 'Categoria', format: (v: any, names: Names) => (v ? names[v] ?? 'Categoria eliminada' : '') };

const MATERIAL_CONFIG: EntityConfig = {
    label: (d) => d?.name ?? 'Material',
    categories: 'categories_materials',
    fields: {
        name: { label: 'Nombre' },
        sku: { label: 'SKU' },
        type: { label: 'Tipo', format: fromMap(MATERIAL_TYPES) },
        category_key: categoryField,
        measure: { label: 'Tipo de medida', format: fromMap(MEASURES) },
        metric: { label: 'Unidad de medida', format: (v) => (v?.symbol ? `${v.name ?? ''} (${v.symbol})`.trim() : '') },
        movement_type: { label: 'Manejo de inventario', format: fromMap(MOVEMENT_TYPES) },
        operating_supply: { label: 'Insumo operativo', format: yesNo },
        expiration_type: { label: 'Tipo de vencimiento', format: fromMap(EXPIRATION_TYPES) },
        expiry_time: { label: 'Tiempo de caducidad' },
        reference_price: { label: 'Costo de referencia', format: money },
        performance: { label: 'Rendimiento', format: num },
        intructions: { label: 'Metodo de elaboracion' },
        active: { label: 'Estado', format: status },
    },
    arrays: {
        recipe: { label: 'Materiales', section: 'materiales', item: Items.semiFinishedLine },
        outputs: { label: 'Salidas', section: 'salidas', item: Items.output },
        areas: { label: 'Areas de produccion', section: 'ubicaciones', item: Items.reference('Area eliminada') },
        stock_config: { label: 'Ubicaciones', section: 'ubicaciones', item: Items.stockArea },
    },
    // `materials`/`used_ingredients` se derivan de `recipe`; `category` es la copia de `category_key`;
    // `unit_change` es el candado del cambio de unidad (que escribe su propio registro).
    ignore: ['materials', 'used_ingredients', 'unit_change', 'category'],
    // El editor de semielaborados copia el rendimiento de la salida principal al campo raiz
    // `performance` (semi-finished-modal, changePerformance): ya se registra en Salidas y con su
    // unidad, asi que en la raiz seria una fila repetida. En documentos sin `outputs` se registra.
    ignoreWhen: (data) => ((data?.outputs || []).length ? ['performance'] : []),
};

const RECIPE_CONFIG: EntityConfig = {
    label: (d) => d?.name ?? 'Receta',
    categories: 'categories_menu',
    fields: {
        name: { label: 'Nombre' },
        sku: { label: 'SKU' },
        intructions: { label: 'Metodo de elaboracion' },
        active: { label: 'Estado', format: status },
        category_key: categoryField,
    },
    arrays: {
        recipe: { label: 'Materiales', section: 'materiales', item: Items.recipeLine },
        sales_disposables: { label: 'Desechables de venta', section: 'desechables', item: Items.disposableLine(true) },
        production_disposables: { label: 'Desechables de produccion', section: 'desechables', item: Items.disposableLine(false) },
        areas: { label: 'Areas de disponibilidad', section: 'ubicaciones', item: Items.reference('Area eliminada') },
        modifiers: { label: 'Modificadores', section: 'modificadores', item: Items.reference('Modificador eliminado') },
    },
    ignore: ['materials', 'category'],
};

const MODIFIER_CONFIG: EntityConfig = {
    label: (d) => d?.name ?? 'Modificador',
    categories: 'categories_modifiers',
    fields: {
        name: { label: 'Nombre' },
        required: { label: 'Obligatorio', format: yesNo },
        multiple: { label: 'Seleccion multiple', format: yesNo },
        default: { label: 'Limite obligatorio', format: yesNo },
        limit: { label: 'Limite de seleccion', format: num },
        active: { label: 'Estado', format: status },
        category_key: categoryField,
    },
    arrays: {
        options: { label: 'Opciones', section: 'opciones', item: Items.modifierOption },
    },
    ignore: ['used_ingredients', 'recipes', 'category'],
};

const PRESENTATION_CONFIG: EntityConfig = {
    label: (d) => d?.name ?? 'Presentacion',
    fields: {
        name: { label: 'Nombre', section: 'presentaciones' },
        display_quantity: { label: 'Cantidad', format: num, section: 'presentaciones' },
        display_unit: { label: 'Unidad', format: (v) => v?.symbol ?? '', section: 'presentaciones' },
        quantity: { label: 'Equivalencia en unidad base', format: num, section: 'presentaciones' },
        active: { label: 'Estado', format: (v) => (v === false ? 'Inactiva' : 'Activa'), section: 'presentaciones' },
    },
    arrays: {},
    ignore: [],
};

// ─── Diff ───────────────────────────────────────────────────────────────────────────────────

function changedKeys(before: any, after: any, config: EntityConfig): Set<string> {
    const dynamic = config.ignoreWhen ? [...config.ignoreWhen(before), ...config.ignoreWhen(after)] : [];
    const ignore = new Set([...GLOBAL_IGNORE, ...config.ignore, ...dynamic]);
    const keys = new Set<string>();
    for (const key of new Set([...Object.keys(before || {}), ...Object.keys(after || {})])) {
        if (ignore.has(key)) continue;
        if (stableStringify(before?.[key]) !== stableStringify(after?.[key])) keys.add(key);
    }
    return keys;
}

function diffArray(label: string, describer: ItemDescriber, oldArr: any[], newArr: any[]): HistoryChange[] {
    // Un reordenamiento sin otros cambios no genera filas; ids repetidos se emparejan por ocurrencia.
    const index = (list: any[]) => {
        const map = new Map<string, any>();
        const seen = new Map<string, number>();
        list.forEach((item, i) => {
            const base = describer.id(item) || `#${i}`;
            const n = seen.get(base) ?? 0;
            seen.set(base, n + 1);
            map.set(n ? `${base}#${n}` : base, item);
        });
        return map;
    };
    const oldMap = index(oldArr);
    const newMap = index(newArr);
    const details = (item: any) => (describer.details ? describer.details(item) : []);
    const summary = (item: any) => details(item).map((d) => `${d.label}: ${d.value}`).join(' · ');
    const cambios: HistoryChange[] = [];

    for (const [id, oldItem] of oldMap) {
        if (newMap.has(id)) continue;
        const name = describer.name(oldItem);
        cambios.push({ campo: `${label} · ${name}`, valorAnterior: summary(oldItem) || name, valorNuevo: '', tipo: 'removed', grupo: label, elemento: name, atributo: null });
    }
    for (const [id, newItem] of newMap) {
        const oldItem = oldMap.get(id);
        const name = describer.name(newItem);
        if (oldItem === undefined) {
            cambios.push({ campo: `${label} · ${name}`, valorAnterior: '', valorNuevo: summary(newItem) || name, tipo: 'added', grupo: label, elemento: name, atributo: null });
            continue;
        }
        const oldDetails = details(oldItem);
        for (const detail of details(newItem)) {
            const previous = oldDetails.find((d) => d.label === detail.label)?.value ?? '';
            if (previous === detail.value) continue;
            cambios.push({ campo: `${label} · ${name} · ${detail.label}`, valorAnterior: previous, valorNuevo: detail.value, tipo: 'updated', grupo: label, elemento: name, atributo: detail.label });
        }
    }
    return cambios;
}

/** `owner`: entidad hija (presentacion) cuyos campos escalares se agrupan bajo su nombre. */
function diffDocuments(before: any, after: any, config: EntityConfig, names: Names, keys: Set<string>, prefix: string,
    owner: { grupo: string; elemento: string } | null): Map<HistorySection, HistoryChange[]> {
    const bySection = new Map<HistorySection, HistoryChange[]>();
    const push = (section: HistorySection, cambio: HistoryChange) => {
        const list = bySection.get(section) ?? [];
        list.push(prefix ? { ...cambio, campo: `${prefix} · ${cambio.campo}` } : cambio);
        bySection.set(section, list);
    };

    for (const key of keys) {
        const arrayConfig = config.arrays[key];
        const oldValue = before?.[key];
        const newValue = after?.[key];

        if (arrayConfig && (Array.isArray(oldValue) || Array.isArray(newValue))) {
            diffArray(arrayConfig.label, arrayConfig.item(names), oldValue || [], newValue || [])
                .forEach((c) => push(arrayConfig.section, c));
            continue;
        }

        const field = config.fields[key];
        const format = field?.format ? (v: any) => field.format!(v, names) : formatGeneric;
        const valorAnterior = format(oldValue);
        const valorNuevo = format(newValue);
        // Diferencia solo de forma (ej. copia embebida con otros campos internos): no se muestra.
        if (valorAnterior === valorNuevo) continue;
        const atributo = field?.label ?? humanize(key);
        push(field?.section ?? 'detalles', {
            campo: atributo, valorAnterior, valorNuevo,
            ...(owner ? { tipo: 'updated' as HistoryChangeType, grupo: owner.grupo, elemento: owner.elemento } : { grupo: null, elemento: null }),
            atributo,
        });
    }
    return bySection;
}
